<script lang="ts">
    import type { NuxtLinkProps } from 'nuxt/app';

    export interface PrimaryButtonProps {
        buttonText?: string;
        buttonHref?: NuxtLinkProps['to'];
        type?: 'button' | 'submit' | 'reset';
        hasIcon?: boolean;
        disabled?: boolean;
        nativeButton?: boolean;
        productButton?: boolean;
        forceProductStyling?: boolean;
        isActive?: boolean;
        isTab?: boolean;
        isLocked?: boolean;
        isDownload?: boolean;
        size?: 'default' | 'small' | 'medium';
        retainSize?: boolean;
        alignment?: 'default' | 'left' | 'center' | 'right';
        openNewTab?: boolean;
        isPending?: boolean;
        id?: string;
        stretch?: boolean;
        variant?: 'primary' | 'secondary' | 'alternative';
        spaceBetween?: boolean;
        ghost?: boolean;
    }
</script>

<script lang="ts" setup>
    import { gsap } from 'gsap';

    const {
        buttonText = 'button',
        buttonHref = '',
        type = 'button',
        hasIcon = false,
        disabled = false,
        nativeButton = false,
        productButton = false,
        forceProductStyling = false,
        isActive = false,
        isTab = false,
        isLocked = false,
        isDownload = false,
        size = 'default',
        retainSize = false,
        alignment = 'default',
        openNewTab = false,
        isPending = false,
        id = '',
        stretch = false,
        variant = 'primary',
        spaceBetween = false,
        ghost = false
    } = defineProps<PrimaryButtonProps>();

    const buttonRef = ref();
    const buttonHoverRef = ref();
    const buttonSpringTl = gsap.timeline({ paused: true });
    const sizeClass = computed(() => {
        if (forceProductStyling || size === 'small') {
            return '--small';
        }
        if (size === 'medium') {
            return '--medium';
        }
        return '';
    });

    const alignmentClass = computed(() => {
        if (alignment === 'center') {
            return '--align-center';
        }

        if (alignment === 'right') {
            return '--align-right';
        }
        return '--align-left';
    });

    const isSecondaryVariant = computed(() => variant === 'secondary');
    const isAlternativeVariant = computed(() => variant === 'alternative');

    function buttonSpringAnimation() {
        buttonSpringTl.play();
    }

    function mouseMove(e: MouseEvent) {
        if (buttonRef.value && !disabled && !isActive) {
            const buttonBoundingClient = buttonRef.value.getBoundingClientRect();
            const mouseOriginX = e.clientX - buttonBoundingClient.left;
            const mouseOriginY = e.clientY - buttonBoundingClient.top;

            if (
                mouseOriginX > 0 &&
                mouseOriginX < buttonBoundingClient.width &&
                mouseOriginY > 0 &&
                mouseOriginY < buttonBoundingClient.height
            ) {
                gsap.to(buttonHoverRef.value, {
                    x: mouseOriginX,
                    y: mouseOriginY,
                    duration: 0.3,
                    ease: 'none'
                });
            }
        }
    }

    onMounted(() => {
        window.addEventListener('mousemove', (e) => mouseMove(e));

        buttonSpringTl.to(buttonRef.value, {
            duration: 0.1,
            ease: 'cubic-bezier(0.18, 0.89, 0.32, 1)',
            scale: 1.1,
            onComplete: () => {
                buttonSpringTl.reverse();
            }
        });
    });

    onUnmounted(() => {
        window.removeEventListener('mousemove', mouseMove);
    });

    defineExpose({ buttonRef });
</script>

<template>
    <button
        v-if="productButton || nativeButton"
        :id="id"
        ref="buttonRef"
        :type="type"
        class="primary-button"
        :class="[
            sizeClass,
            alignmentClass,
            {
                '--small --product': productButton,
                '--disabled': disabled,
                '--no-icon': !hasIcon && !isAlternativeVariant,
                '--alternate-button': isSecondaryVariant,
                '--alternative-button': isAlternativeVariant,
                '--active': isActive,
                '--tab': isTab,
                '--locked': isLocked,
                '--retain-size': retainSize,
                '--is-pending': isPending,
                '--stretch': stretch,
                '--space-between': spaceBetween,
                '--ghost': ghost
            }
        ]"
        :disabled="disabled || isPending"
        @click="buttonSpringAnimation()"
    >
        <slot>
            <span>
                {{ buttonText }}
            </span>

            <slot name="customIcon">
                <span
                    v-if="isAlternativeVariant"
                    class="primary-button__icon-square"
                >
                    <ChevronRightIcon />
                </span>
                <ChevronRightIcon v-else-if="hasIcon" />
                <img
                    v-if="isLocked"
                    src="~/assets/icons/lock-keyhole.svg"
                    alt="navigation arrow"
                    aria-hidden="true"
                />
            </slot>
        </slot>

        <div class="desktop-hover-wrapper">
            <div
                ref="buttonHoverRef"
                class="desktop-hover"
            />
        </div>
    </button>

    <NuxtLink
        v-else
        v-bind="isDownload ? { download: '' } : {}"
        :id="id"
        class="primary-button"
        :class="[
            sizeClass,
            alignmentClass,
            {
                '--disabled': disabled,
                '--product': forceProductStyling,
                '--no-icon': !hasIcon && !isAlternativeVariant,
                '--alternate-button': isSecondaryVariant,
                '--alternative-button': isAlternativeVariant,
                '--active': isActive,
                '--tab': isTab,
                '--locked': isLocked,
                '--retain-size': retainSize,
                '--has-download': isDownload,
                '--stretch': stretch,
                '--space-between': spaceBetween,
                '--ghost': ghost
            }
        ]"
        :tabindex="disabled ? '-1' : undefined"
        :to="disabled ? '' : buttonHref"
        :target="isDownload || openNewTab ? '_blank' : '_self'"
    >
        <span>
            {{ buttonText }}
        </span>

        <slot name="customIcon">
            <span
                v-if="isAlternativeVariant"
                class="primary-button__icon-square"
            >
                <ChevronRightIcon />
            </span>

            <ChevronRightIcon v-if="hasIcon && !isDownload && !isAlternativeVariant" />

            <svg
                v-if="hasIcon && isDownload && !isAlternativeVariant"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                alt="download"
                aria-hidden="true"
            >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line
                    x1="12"
                    x2="12"
                    y1="15"
                    y2="3"
                />
            </svg>
        </slot>

        <div
            ref="buttonRef"
            class="desktop-hover-wrapper"
        >
            <div
                ref="buttonHoverRef"
                class="desktop-hover"
            />
        </div>
    </NuxtLink>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/basics/primary-button.scss';

    :deep(span.iconify) {
        color: inherit;
    }

    .primary-button > *:not(.desktop-hover-wrapper) {
        z-index: 1;
    }
</style>
